import { Controller } from "@hotwired/stimulus"

// Drag & drop dropzone for the support contact dialog's attachment field.
// Purely presentational: it keeps the native multiple <input type="file"> in
// sync via a DataTransfer so the standard multipart form submit is unchanged.
// Accumulates files across drops/picks, renders removable chips with image
// thumbnails, and validates size/type client-side (mirrors the model).
export default class extends Controller {
  static targets = ["input", "dropzone", "list", "error"]

  static values = {
    maxSize: { type: Number, default: 10485760 }, // 10MB, matches ContactRequest::ATTACHMENT_MAX_BYTES
    tooLargeMessage: String,
    invalidTypeMessage: String,
    removeLabel: String,
  }

  connect() {
    this.files = []
    this.objectUrls = []
    this.renderList()
  }

  disconnect() {
    this.revokeObjectUrls()
  }

  // --- Drag & drop ---

  dragover(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-green-500", "bg-green-50")
  }

  dragleave(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-green-500", "bg-green-50")
  }

  drop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-green-500", "bg-green-50")
    this.addFiles(Array.from(event.dataTransfer.files))
  }

  browse() {
    this.inputTarget.click()
  }

  inputChanged() {
    this.addFiles(Array.from(this.inputTarget.files))
  }

  // --- File handling ---

  addFiles(incoming) {
    const errors = []

    incoming.forEach((file) => {
      if (!this.acceptedType(file)) {
        errors.push(this.formatMessage(this.invalidTypeMessageValue, file.name))
        return
      }
      if (file.size > this.maxSizeValue) {
        errors.push(this.formatMessage(this.tooLargeMessageValue, file.name))
        return
      }
      if (this.alreadyAdded(file)) return

      this.files.push(file)
    })

    this.showErrors(errors)
    this.syncInput()
    this.renderList()
  }

  removeFile(event) {
    const index = Number(event.currentTarget.dataset.index)
    this.files.splice(index, 1)
    this.syncInput()
    this.renderList()
  }

  acceptedType(file) {
    return file.type.startsWith("image/") || file.type.startsWith("video/")
  }

  alreadyAdded(file) {
    return this.files.some((f) => f.name === file.name && f.size === file.size)
  }

  // Mirror our in-memory list back onto the real input so the form submits it.
  syncInput() {
    const transfer = new DataTransfer()
    this.files.forEach((file) => transfer.items.add(file))
    this.inputTarget.files = transfer.files
  }

  // --- Rendering ---

  renderList() {
    this.revokeObjectUrls()
    this.listTarget.innerHTML = ""

    this.files.forEach((file, index) => {
      this.listTarget.appendChild(this.buildChip(file, index))
    })
  }

  buildChip(file, index) {
    const chip = document.createElement("div")
    chip.className =
      "flex items-center justify-between gap-3 p-2 bg-gray-50 border border-gray-200 rounded-md"

    const left = document.createElement("div")
    left.className = "flex items-center gap-3 min-w-0 flex-1"
    left.appendChild(this.buildPreview(file))

    const meta = document.createElement("div")
    meta.className = "min-w-0 flex-1"
    meta.innerHTML = `
      <p class="text-sm font-medium text-gray-900 truncate">${this.escape(file.name)}</p>
      <p class="text-xs text-gray-500">${this.formatFileSize(file.size)}</p>
    `
    left.appendChild(meta)

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "ml-2 p-1 text-gray-400 hover:text-red-500 shrink-0"
    remove.setAttribute("aria-label", this.removeLabelValue)
    remove.dataset.action = "click->support-attachments#removeFile"
    remove.dataset.index = index
    remove.innerHTML = `
      <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
      </svg>
    `

    chip.appendChild(left)
    chip.appendChild(remove)
    return chip
  }

  buildPreview(file) {
    if (file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file)
      this.objectUrls.push(url)
      const img = document.createElement("img")
      img.src = url
      img.alt = file.name
      img.className = "h-10 w-10 object-cover rounded shrink-0"
      return img
    }

    const wrap = document.createElement("div")
    wrap.className = "h-10 w-10 flex items-center justify-center bg-gray-100 rounded shrink-0"
    wrap.innerHTML = `
      <svg class="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    `
    return wrap
  }

  showErrors(errors) {
    if (!this.hasErrorTarget) return

    if (errors.length === 0) {
      this.errorTarget.textContent = ""
      this.errorTarget.classList.add("hidden")
      return
    }

    this.errorTarget.textContent = errors.join(" ")
    this.errorTarget.classList.remove("hidden")
  }

  // --- Helpers ---

  revokeObjectUrls() {
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url))
    this.objectUrls = []
  }

  formatMessage(template, filename) {
    return (template || "").replace("%{filename}", filename)
  }

  escape(value) {
    const div = document.createElement("div")
    div.textContent = value
    return div.innerHTML
  }

  formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / 1048576).toFixed(1)} MB`
  }
}
